/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import InquiryToCustomer from "@/src/types/Entities/InquiryToCustomer";
import { fetchCollection, fetchResource, queryKeys, updateResource } from "@/src/services/reactQuery/reactQueryService";

const inquiryToCustomerApiUri = "/inquiry_to_customers";

export type InquiryToCustomerFilters = {
  identifier?: string;
  productId?: number;
  calendlyEvent?: string;
  status?: InquiryToCustomer["status"];
  "status[0]"?: InquiryToCustomer["status"];
  "status[1]"?: InquiryToCustomer["status"];
  "status[2]"?: InquiryToCustomer["status"];
};

type InquiryToCustomerQueryOptions = {
  enabled?: boolean;
  queryKeyFilters?: InquiryToCustomerFilters & { customerId?: number };
};

export type UpdateInquiryToCustomerData = Partial<
  Pick<
    InquiryToCustomer,
    | "status"
    | "doneBy"
    | "inquiryTextInformationCustomer"
    | "inquiryTextDocumentsCustomer"
    | "inquiryTextAppointmentCustomer"
    | "requestedDocuments"
    | "inquiryDecisionAnswer"
    | "linkUrl"
  >
>;

type UpdateInquiryToCustomerVariables = {
  id: number;
  data: UpdateInquiryToCustomerData;
};

export const useInquiryToCustomers = (filters: InquiryToCustomerFilters = {}, options: InquiryToCustomerQueryOptions = {}) =>
  useQuery<InquiryToCustomer[]>({
    queryKey: queryKeys.collection(inquiryToCustomerApiUri, options.queryKeyFilters ?? filters),
    queryFn: () => fetchCollection(inquiryToCustomerApiUri, filters),
    enabled: options.enabled,
  });

export const useInquiryToCustomer = (id: number, enabled = true) =>
  useQuery<InquiryToCustomer>({
    queryKey: queryKeys.item(inquiryToCustomerApiUri, id),
    queryFn: () => fetchResource(inquiryToCustomerApiUri, id),
    enabled,
  });

export const useUpdateInquiryToCustomer = () => {
  const { invalidateInquiryToCustomer } = useInvalidateInquiryToCustomer();

  return useMutation<InquiryToCustomer, Error, UpdateInquiryToCustomerVariables>({
    mutationFn: ({ id, data }) => updateResource({ uri: inquiryToCustomerApiUri, id, data }),
    onSuccess: invalidateInquiryToCustomer,
  });
};

export const useInvalidateInquiryToCustomer = () => {
  const queryClient = useQueryClient();

  return {
    invalidateInquiryToCustomer: () => {
      return queryClient.invalidateQueries({
        queryKey: queryKeys.all(inquiryToCustomerApiUri),
      });
    },
  };
};
